import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Wrench, Shuffle, DollarSign, ArrowRight,
  ArrowRightLeft, Clock, TrendingUp, RefreshCw, AlertTriangle,
} from 'lucide-react';
import { getDashboard } from '../api/dashboard';
import { useAuth } from '../context/AuthContext';

const TOOL_COLORS = [
  'bg-violet-500', 'bg-blue-500', 'bg-emerald-500', 'bg-amber-500',
  'bg-rose-500',   'bg-cyan-500',  'bg-indigo-500',  'bg-teal-500',
];

const DEPT_COLORS = [
  'bg-violet-400', 'bg-blue-400', 'bg-emerald-400', 'bg-amber-400',
  'bg-rose-400',   'bg-cyan-400',  'bg-indigo-400',  'bg-teal-400',
];

function StatCard({ icon: Icon, label, value, sub, color, to }) {
  const card = (
    <div className={`rounded-2xl p-5 flex items-start gap-4 bg-white shadow-sm border border-slate-100 hover:shadow-md transition-shadow ${to ? 'cursor-pointer' : ''}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={22} className="text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-slate-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-slate-800 mt-0.5">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
  return to ? <Link to={to}>{card}</Link> : card;
}

function ToolBar({ name, count, maxCount, colorClass }) {
  const pct = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="w-36 text-sm text-slate-600 truncate">{name}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
        <div
          className={`h-2.5 rounded-full ${colorClass} transition-all duration-700`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-8 text-right text-sm font-semibold text-slate-700">{count}</span>
    </div>
  );
}

function DeptBar({ name, count, maxCount, colorClass }) {
  const pct = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="w-36 text-sm text-slate-600 truncate">{name}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
        <div
          className={`h-2.5 rounded-full ${colorClass} transition-all duration-700`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-8 text-right text-sm font-semibold text-slate-700">{count}</span>
    </div>
  );
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  < 1)  return 'just now';
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function initials(name = '') {
  return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
}

const AVATAR_COLORS = [
  'bg-violet-100 text-violet-700',
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
];
function avatarColor(name = '') {
  const idx = name.charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

export default function Dashboard() {
  const { admin } = useAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getDashboard();
      setData(res.data);
      setLastRefresh(new Date());
    } catch {
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <p className="text-red-500">{error}</p>
      <button onClick={load} className="btn-primary text-sm">Retry</button>
    </div>
  );

  const { stats, tool_breakdown, dept_breakdown, recent_employees, recent_migrations, expiring_soon } = data;
  const maxToolCount = Math.max(...tool_breakdown.map(t => Number(t.user_count)), 1);
  const maxDeptCount = Math.max(...dept_breakdown.map(d => Number(d.count)), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            {greeting()}, {admin?.name?.split(' ')[0] || 'Admin'} 👋
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Here's what's happening with your AI tools today.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
          title="Refresh"
        >
          <RefreshCw size={15} />
          <span className="hidden sm:inline">
            Refreshed {timeAgo(lastRefresh)}
          </span>
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard
          icon={Users}
          label="Total Employees"
          value={stats.total_employees}
          sub={`${stats.active_allocations} with active tools`}
          color="bg-violet-500"
          to="/employees"
        />
        <StatCard
          icon={Wrench}
          label="Active AI Tools"
          value={stats.total_tools}
          sub="Currently in use"
          color="bg-blue-500"
          to="/tools"
        />
        <StatCard
          icon={ArrowRightLeft}
          label="Active Allocations"
          value={stats.active_allocations}
          sub="Assigned right now"
          color="bg-emerald-500"
        />
        <StatCard
          icon={Shuffle}
          label="Migrations This Month"
          value={stats.migrations_this_month}
          sub="Tool switches"
          color="bg-amber-500"
          to="/migrations"
        />
        <StatCard
          icon={DollarSign}
          label="Est. Monthly Cost"
          value={`$${stats.monthly_cost.toFixed(2)}`}
          sub="Current month"
          color="bg-rose-500"
          to="/reports/cost"
        />
      </div>

      {/* Expiring Soon Banner */}
      {expiring_soon.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              {expiring_soon.length} allocation{expiring_soon.length > 1 ? 's' : ''} expiring in the next 7 days
            </p>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {expiring_soon.map(e => (
                <span
                  key={e.id}
                  className="text-xs bg-amber-100 text-amber-700 rounded-full px-2 py-0.5"
                >
                  {e.name} — {e.tool_name} (expires {new Date(e.end_date).toLocaleDateString()})
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Middle row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tool Breakdown */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-700">Users per AI Tool</h2>
            <Link to="/tools" className="text-xs text-violet-600 hover:underline flex items-center gap-1">
              Manage <ArrowRight size={12} />
            </Link>
          </div>
          {tool_breakdown.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No tools found.</p>
          ) : (
            <div className="space-y-0.5">
              {tool_breakdown.map((t, i) => (
                <ToolBar
                  key={t.id}
                  name={t.name}
                  count={Number(t.user_count)}
                  maxCount={maxToolCount}
                  colorClass={TOOL_COLORS[i % TOOL_COLORS.length]}
                />
              ))}
            </div>
          )}
          {/* Legend */}
          <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
            {tool_breakdown.map((t, i) => (
              <div key={t.id} className="flex items-center gap-1.5 text-xs text-slate-500">
                <span className={`w-2 h-2 rounded-full inline-block ${TOOL_COLORS[i % TOOL_COLORS.length]}`} />
                {t.name} — ${Number(t.monthly_price).toFixed(2)}/mo
              </div>
            ))}
          </div>
        </div>

        {/* Department Breakdown */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-700">Employees per Department</h2>
            <Link to="/employees" className="text-xs text-violet-600 hover:underline flex items-center gap-1">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          {dept_breakdown.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No department data.</p>
          ) : (
            <div className="space-y-0.5">
              {dept_breakdown.map((d, i) => (
                <DeptBar
                  key={d.department}
                  name={d.department}
                  count={Number(d.count)}
                  maxCount={maxDeptCount}
                  colorClass={DEPT_COLORS[i % DEPT_COLORS.length]}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Employees */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-700 flex items-center gap-2">
              <Users size={16} className="text-violet-500" /> Recent Employees
            </h2>
            <Link to="/employees" className="text-xs text-violet-600 hover:underline flex items-center gap-1">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          {recent_employees.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No employees yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recent_employees.map(emp => (
                <li key={emp.id} className="flex items-center gap-3 py-2.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarColor(emp.name)}`}>
                    {initials(emp.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{emp.name}</p>
                    <p className="text-xs text-slate-400 truncate">{emp.department}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {emp.tool_name ? (
                      <span className="text-xs bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full font-medium">
                        {emp.tool_name}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">No tool</span>
                    )}
                    <p className="text-xs text-slate-400 mt-0.5">{timeAgo(emp.created_at)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent Migrations */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-700 flex items-center gap-2">
              <ArrowRightLeft size={16} className="text-amber-500" /> Recent Migrations
            </h2>
            <Link to="/migrations" className="text-xs text-violet-600 hover:underline flex items-center gap-1">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          {recent_migrations.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No migrations yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recent_migrations.map(m => (
                <li key={m.id} className="flex items-center gap-3 py-2.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarColor(m.employee_name)}`}>
                    {initials(m.employee_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{m.employee_name}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-xs text-slate-400 truncate">{m.from_tool || '—'}</span>
                      <ArrowRight size={10} className="text-slate-300 flex-shrink-0" />
                      <span className="text-xs text-emerald-600 font-medium truncate">{m.to_tool}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                      {m.department}
                    </span>
                    <p className="text-xs text-slate-400 mt-0.5">{timeAgo(m.created_at)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
        <h2 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <TrendingUp size={16} className="text-blue-500" /> Quick Actions
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Manage Employees', to: '/employees',    color: 'bg-violet-50 text-violet-700 hover:bg-violet-100', icon: Users },
            { label: 'Manage Tools',     to: '/tools',        color: 'bg-blue-50 text-blue-700 hover:bg-blue-100',       icon: Wrench },
            { label: 'View Migrations',  to: '/migrations',   color: 'bg-amber-50 text-amber-700 hover:bg-amber-100',    icon: Shuffle },
            { label: 'Cost Report',      to: '/reports/cost', color: 'bg-rose-50 text-rose-700 hover:bg-rose-100',       icon: DollarSign },
          ].map(({ label, to, color, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className={`rounded-xl px-4 py-3 flex items-center gap-2.5 font-medium text-sm transition-colors ${color}`}
            >
              <Icon size={15} />
              {label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
